import {
  CONTENT_ID_CLAIM_RELEASE_REQUESTS_LOADED,
  CONTENT_ID_CLAIM_RELEASE_REQUESTS_CLEAR,
  CONTENT_ID_CLAIM_RELEASE_REQUEST_DETAIL_LOADED,
  CONTENT_ID_CLAIM_RELEASE_REQUEST_DETAIL_CLEAR,
  CONTENT_ID_CLAIMS_STATUS_PATCH,
} from '../../actionTypes';
import * as api from '../../apiHelper';
import { arriveNotice } from '../../noticeActions';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../../fetchLoaderActions';

function claimReleaseRequestsLoaded(claimReleaseRequests) {
  return {
    type: CONTENT_ID_CLAIM_RELEASE_REQUESTS_LOADED,
    payload: { claimReleaseRequests },
  };
}

function claimReleaseRequestsClear() {
  return { type: CONTENT_ID_CLAIM_RELEASE_REQUESTS_CLEAR };
}

function claimReleaseRequestDetailLoaded(claimReleaseRequestDetail) {
  return {
    type: CONTENT_ID_CLAIM_RELEASE_REQUEST_DETAIL_LOADED,
    payload: { claimReleaseRequestDetail },
  };
}

function claimReleaseRequestDetailClear() {
  return { type: CONTENT_ID_CLAIM_RELEASE_REQUEST_DETAIL_CLEAR };
}

async function getClaimReleaseRequestsApi(token, params) {
  const response = await api.get({
    path: 'youtube-cid/claims/monetization-requests/release-requests',
    token,
    params,
    version: 'gateway',
  });
  return response.json();
}

async function getClaimReleaseRequestApi(token, requestId) {
  const response = await api.get({
    path: `youtube-cid/claims/monetization-requests/release-requests/${requestId}`,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function postClaimReleaseRequestApi(token, body) {
  const response = await api.post({
    path: 'youtube-cid/claims/monetization-requests/release-requests',
    token,
    body,
    version: 'gateway',
  });
  return response.json();
}

export function initiateFetchClaimReleaseRequests(params) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { dataLoading },
    } = getState();

    if (paymentPending) return;

    if (!dataLoading) {
      dispatch(setFetchLoaderLoading());
    }

    try {
      const claimReleaseRequests = await getClaimReleaseRequestsApi(token, params);
      dispatch(claimReleaseRequestsLoaded(claimReleaseRequests));
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
      }
    } finally {
      dispatch(setFetchLoaderReset());
    }
  };
}

export function clearClaimReleaseRequests() {
  return (dispatch) => {
    dispatch(claimReleaseRequestsClear());
  };
}

export function initiateFetchClaimReleaseRequestDetail(requestId) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { dataLoading },
    } = getState();

    if (paymentPending) return;

    if (!dataLoading) {
      dispatch(setFetchLoaderLoading());
    }

    try {
      const claimReleaseRequestDetail = await getClaimReleaseRequestApi(token, requestId);
      dispatch(claimReleaseRequestDetailLoaded(claimReleaseRequestDetail));
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
      }
    } finally {
      dispatch(setFetchLoaderReset());
    }
  };
}

export function clearClaimReleaseRequestDetail() {
  return (dispatch) => {
    dispatch(claimReleaseRequestDetailClear());
  };
}

export function initiateCreateClaimReleaseRequest(body) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { submissionLoading },
    } = getState();

    if (paymentPending) return null;

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    try {
      const response = await postClaimReleaseRequestApi(token, body);
      dispatch(arriveNotice(response.message || 'Release request submitted.', 'ok'));
      return response;
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
      }
      return null;
    } finally {
      dispatch(setSubmissionLoaderReset());
    }
  };
}

export function initiateFetchAssetClaimsCount(assetId) {
  return async (_dispatch, getState) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.get({
        path: `youtube-cid/assets/${assetId}/claims`,
        token,
        params: { limit: 1, skip: 0 },
        version: 'gateway',
      });
      const data = await response.json();
      return data?.pagination?.totalResults ?? data?.total ?? null;
    } catch (e) {
      return null;
    }
  };
}

async function postRevertReleaseRequestApi(token, body) {
  const response = await api.post({
    path: 'youtube-cid/claims/monetization-requests/release-requests/revert',
    token,
    body,
    version: 'gateway',
  });
  return response.json();
}

export function initiateRevertClaimReleaseRequest(body) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { submissionLoading },
    } = getState();
    if (paymentPending) return null;
    if (!submissionLoading) dispatch(setSubmissionLoaderLoading());
    try {
      const response = await postRevertReleaseRequestApi(token, body);
      dispatch(arriveNotice(response.message || 'Release request reverted.', 'ok'));
      return response;
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
        // Only patch the local claim status when the error is specifically a
        // "under review" conflict (409). For 500s or network errors the backend
        // hasn't changed anything, so patching the UI would show wrong data.
        const isUnderReviewConflict = e.message
          && e.message.toLowerCase().includes('under review');
        const claimIds = (body.claims || []).map((c) => c.claim_id).filter(Boolean);
        if (isUnderReviewConflict && claimIds.length > 0) {
          dispatch({
            type: CONTENT_ID_CLAIMS_STATUS_PATCH,
            payload: { claimIds, status: 'RELEASE_UNDER_REVIEW' },
          });
        }
      }
      return null;
    } finally {
      dispatch(setSubmissionLoaderReset());
    }
  };
}

export function initiateFetchActiveAssetReleaseRequest(assetId) {
  return async (_dispatch, getState) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.get({
        path: 'youtube-cid/claims/monetization-requests/release-requests',
        token,
        params: { assetId, limit: 1, skip: 0 },
        version: 'gateway',
      });
      const data = await response.json();
      // Return the first non-COMPLETED request for this asset, or null
      const requests = data?.data || [];
      return requests.find((r) => r.requestStatus !== 'COMPLETED') || null;
    } catch (e) {
      return null;
    }
  };
}

export function initiateReleaseAllAssetClaims(assetId) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { submissionLoading },
    } = getState();

    if (paymentPending) return null;

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    try {
      const response = await api.get({
        path: `youtube-cid/assets/${assetId}/claims`,
        token,
        params: { limit: 10000, skip: 0 },
        version: 'gateway',
      });
      const claimsData = await response.json();
      const claims = claimsData?.data || [];

      if (claims.length === 0) {
        dispatch(arriveNotice('No claims found for this asset.'));
        return null;
      }

      // Filter out claims already in the release pipeline or fully released
      const releaseExcludedStatuses = ['RELEASE_REQUESTED', 'RELEASE_UNDER_REVIEW', 'RELEASED'];
      const eligibleClaims = claims.filter((c) => !releaseExcludedStatuses.includes(c.status));

      if (eligibleClaims.length === 0) {
        dispatch(arriveNotice('All claims for this asset have already been released or are pending release.'));
        return null;
      }

      /* eslint-disable camelcase */
      const body = {
        claims: eligibleClaims.map((c) => ({
          claim_id: c.id,
          asset_id: c.assetId,
          video_id: c.videoId,
          video_title: c.videoTitle || undefined,
          previous_status: c.status,
        })),
      };
      /* eslint-enable camelcase */

      const result = await postClaimReleaseRequestApi(token, body);
      dispatch(arriveNotice(
        result.message || `Release request submitted for ${eligibleClaims.length} claim${eligibleClaims.length === 1 ? '' : 's'}.`,
        'ok',
      ));
      return result;
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
      }
      return null;
    } finally {
      dispatch(setSubmissionLoaderReset());
    }
  };
}
